"use client";

import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Eye, BarChart3, AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { BUCKET_NAME } from "@/lib/constants";
import { compatible_previews } from "@/services/preview-service";
import { updatePreviewImage } from "@/services/preview-service";
import { DeleteDialog } from "@/components/preview/DeleteDialog";
import UpdatePreviewModal from "@/components/preview/create-preview-modal";
import AnalyticsDashboard from "@/components/analytics/detailed-analytics";
import type { PreviewData, CropSettings } from "@/lib/types";
import { PreviewHeader } from "@/components/preview/preview-header";
import { AnalyticsEmptyState } from "@/components/preview/analytics-empty-state";
import PreviewLayout from "@/components/preview/preview-layout";
import { useTranslation } from "react-i18next";
import { PreviewNotFound } from "@/components/preview/PreviewNotFound";

export default function Preview() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isUpdateImageModalOpen, setIsUpdateImageModalOpen] = useState(false);
  const [isUpdatingImage, setIsUpdatingImage] = useState(false);
  const [compatible_sub_previews, setCompatible_sub_previews] = useState<any[]>(
    []
  );
  const [selectedSubPreview, setselectedSubPreview] =
    useState<PreviewData | null>(null);

  // Edit state
  const [editName, setEditName] = useState("");
  const [editShareable, setEditShareable] = useState(false);
  const [editSubPreviewId, setEditSubPreviewId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editTags, setEditTags] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation("common");

  useEffect(() => {
    async function fetchPreviewDetails() {
      try {
        if (!id) {
          throw new Error("Preview ID is required");
        }

        setLoading(true);
        setError(null);

        const {
          data: { user },
        } = await supabase.auth.getUser();
        const { data: previewObj, error } = await supabase
          .from("previews")
          .select("*, model:available_models(name,settings)")
          .eq("user_id", user?.id)
          .eq("id", id)
          .single();

        if (error) throw error;
        if (!previewObj) throw new Error("Preview not found");
        setPreview(previewObj);

        // Initialize edit state
        setEditName(previewObj.name);
        setEditShareable(previewObj.is_sharable || false);
        setEditSubPreviewId(previewObj.sub_preview_id || null);
        setEditProjectId(previewObj.project_id || null);
        setEditTags(previewObj.tags || []);

        // Get compatible models then compatible previews
        if (previewObj.model_uuid) {
          const all_compatible_previews = await compatible_previews(
            previewObj.model_uuid
          );
          setCompatible_sub_previews(all_compatible_previews);
          const selectedSubPreview =
            all_compatible_previews.find(
              (p) => previewObj.sub_preview_id === p.id
            ) || null;
          setselectedSubPreview(selectedSubPreview);
        }
      } catch (err) {
        console.error("Error fetching preview:", err);
        setError(err instanceof Error ? err.message : "Failed to load preview");
      } finally {
        setLoading(false);
      }
    }

    fetchPreviewDetails();
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSaveChanges = async () => {
    if (!preview) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("previews")
        .update({
          name: editName,
          is_sharable: editShareable,
          sub_preview_id: editSubPreviewId,
          project_id: editProjectId,
          tags: editTags,
        })
        .eq("id", preview.id);

      if (error) throw error;

      // Update local state
      setPreview({
        ...preview,
        name: editName,
        is_sharable: editShareable,
        sub_preview_id: editSubPreviewId,
        project_id: editProjectId,
        tags: editTags,
      });

      setIsEditing(false);
      toast.success(t("common:previewViewer.toast.update.success"), {
        description: t("common:previewViewer.toast.update.successDescription"),
      });
    } catch (err) {
      console.error("Error updating preview:", err);
      toast.error(t("common:previewViewer.toast.update.failed"), {
        description: t("common:previewViewer.toast.update.failedDescription"),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    // Reset edit values to current preview values
    if (preview) {
      setEditName(preview.name);
      setEditShareable(preview.is_sharable || false);
      setEditSubPreviewId(preview.sub_preview_id || null);
      setEditProjectId(preview.project_id || null);
      setEditTags(preview.tags || []);
    }
    setIsEditing(false);
  };

  const handleDelete = async () => {
    if (!preview) return;

    try {
      // Delete the file from storage
      const { error: storageError } = await supabase.storage
        .from(BUCKET_NAME)
        .remove([preview.asset_path]);

      if (storageError) {
        console.error("Error deleting asset from storage:", storageError);
      }

      // Delete the record from the database
      const { error } = await supabase
        .from("previews")
        .delete()
        .eq("id", preview.id);

      if (error) throw error;

      toast.success(t("common:previewViewer.toast.delete.success"), {
        description: t("common:previewViewer.toast.delete.successDescription"),
      });

      // Navigate back to previews list
      navigate("/previews");
    } catch (err) {
      console.error("Error deleting preview:", err);
      toast.error(t("common:previewViewer.toast.delete.failed"), {
        description: t("common:previewViewer.toast.delete.failedDescription"),
      });
    }
  };

  const handleUpdateImage = async (file: File, cropSettings?: CropSettings) => {
    if (!preview || !id) return;

    try {
      setIsUpdatingImage(true);

      const result = await updatePreviewImage(
        file,
        id,
        preview.asset_path,
        cropSettings
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to update preview image");
      }

      // Update local state with new asset path
      setPreview({
        ...preview,
        asset_path: result.newAssetPath || preview.asset_path,
      });

      setIsUpdateImageModalOpen(false);

      toast.success(t("common:previewViewer.toast.imageUpdate.success"), {
        description: t(
          "common:previewViewer.toast.imageUpdate.successDescription"
        ),
      });
    } catch (error) {
      console.error("Error updating image:", error);
      toast.error(t("common:previewViewer.toast.imageUpdate.failed"), {
        description: t(
          "common:previewViewer.toast.imageUpdate.failedDescription"
        ),
      });
    } finally {
      setIsUpdatingImage(false);
    }
  };

  const handleSubPreviewChange = async (subPreviewId: string | null) => {
    if (!preview) return;

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from("previews")
        .update({
          sub_preview_id: subPreviewId,
        })
        .eq("id", preview.id);

      if (error) throw error;

      // Update local state
      setPreview({
        ...preview,
        sub_preview_id: subPreviewId,
      });

      // Update edit state to keep in sync
      setEditSubPreviewId(subPreviewId);

      // Update selectedSubPreview state
      const newSelectedSubPreview = subPreviewId
        ? compatible_sub_previews.find((p) => p.id === subPreviewId) || null
        : null;
      setselectedSubPreview(newSelectedSubPreview);

      toast.success(t("common:previewViewer.toast.subPreview.success"), {
        description: t(
          "common:previewViewer.toast.subPreview.successDescription"
        ),
      });
    } catch (err) {
      console.error("Error updating sub-preview:", err);
      toast.error(t("common:previewViewer.toast.subPreview.failed"), {
        description: t(
          "common:previewViewer.toast.subPreview.failedDescription"
        ),
      });

      // Revert to previous value
      setEditSubPreviewId(preview.sub_preview_id || null);
    } finally {
      setIsSaving(false);
    }
  };

  const handleEnableSharing = () => {
    setEditShareable(true);
    setIsEditing(true);
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="space-y-6">
          {/* Header skeleton */}
          <div className="space-y-4 pb-6 border-b">
            <Skeleton className="h-8 w-32" />
            <div className="space-y-3">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>

          {/* Content skeleton */}
          <div className="space-y-6 lg:hidden">
            <Skeleton className="h-96 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
          <div className="hidden lg:grid lg:grid-cols-5 lg:gap-8">
            <div className="lg:col-span-2">
              <Skeleton className="h-96 w-full" />
            </div>
            <div className="lg:col-span-3">
              <Skeleton className="h-96 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <Alert variant="destructive" className="max-w-2xl mx-auto">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("common:previewViewer.error")}</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <PreviewNotFound onNavigateToList={() => navigate("/previews")} />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <div className="space-y-6">
        <PreviewHeader
          preview={preview}
          onNavigateBack={() => navigate("/previews")}
          formatDate={formatDate}
        />

        <Tabs defaultValue="preview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="preview" className="gap-2">
              <Eye className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("common:previewViewer.preview")}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="analytics"
              disabled={!preview.is_sharable}
              className="gap-2"
            >
              <BarChart3 className="h-4 w-4" />
              <span className="hidden sm:inline">
                {t("common:previewViewer.analytics")}
              </span>
              {!preview.is_sharable && (
                <Badge variant="secondary" className="ml-1 text-xs">
                  {t("common:previewViewer.disabled")}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="preview" className="space-y-0">
            <PreviewLayout
              preview={preview}
              selectedSubPreview={selectedSubPreview}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              editName={editName}
              setEditName={setEditName}
              editShareable={editShareable}
              setEditShareable={setEditShareable}
              editSubPreviewId={editSubPreviewId}
              editProjectId={editProjectId}
              setEditProjectId={setEditProjectId}
              editTags={editTags}
              setEditTags={setEditTags}
              isSaving={isSaving}
              handleSaveChanges={handleSaveChanges}
              handleCancelEdit={handleCancelEdit}
              openDeleteDialog={() => setIsDeleteDialogOpen(true)}
              openUpdateImageModal={() => setIsUpdateImageModalOpen(true)}
              isUpdatingImage={isUpdatingImage}
              formatDate={formatDate}
              compatible_sub_previews={compatible_sub_previews}
              handleSubPreviewChange={handleSubPreviewChange}
            />
          </TabsContent>

          <TabsContent value="analytics" className="space-y-0">
            {preview.is_sharable ? (
              <AnalyticsDashboard previewId={preview.id} />
            ) : (
              <AnalyticsEmptyState onEnableSharing={handleEnableSharing} />
            )}
          </TabsContent>
        </Tabs>
      </div>

      <DeleteDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
        previewName={preview?.name || ""}
      />

      {/* Image Update Modal */}
      {preview && (
        <UpdatePreviewModal
          asChild={false}
          trigger={<></>}
          title={
            <span>
              {t("common:previewViewer.updateLabel", {
                previewName: preview.name,
              })}{" "}
            </span>
          }
          description={
            <div className="flex flex-col space-y-2 text-sm">
              <div>
                {t("common:previewViewer.newImage")}
                <Badge variant="outline" className="mx-3">
                  {t("common:previewViewer.newImage", {
                    ratio: preview.model?.settings?.ratio,
                  })}
                </Badge>
              </div>
            </div>
          }
          upload_text={t("common:previewViewer.updatelabelBtn")}
          open={isUpdateImageModalOpen}
          setOpen={setIsUpdateImageModalOpen}
          onUploadComplete={handleUpdateImage}
          modelUuid={""}
        />
      )}
    </div>
  );
}
